import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import environment from '../env-config';
import {getAuthHeaders} from '../utils/authHeaders';

export const normalizeServerTtsVoices = catalog => !catalog?.enabled || !Array.isArray(catalog.voices) ? []
    : catalog.voices.filter(voice => typeof voice.id === 'string' && /^[a-zA-Z0-9_-]{1,80}$/.test(voice.id)
        && typeof voice.name === 'string' && typeof voice.language === 'string')
        .map(voice => ({id: `server:${voice.id}`, name: voice.name, language: voice.language}));

export default function useServerTtsVoices(open, selectedVoice, setSelectedVoice, storageKey) {
    const {i18n} = useTranslation();
    const [voices, setVoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    useEffect(() => {
        if (!open) return undefined;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        let current = true;
        setVoices([]); setLoading(true); setError(false);
        const base = String(environment.REACT_BACKEND_URL || '').replace(/\/graphql\/?$/, '').replace(/\/$/, '');
        (async () => {
            try {
                const response = await fetch(`${base}/api/article-speech/voices`, {
                    headers: getAuthHeaders({includeRefreshToken: true}), signal: controller.signal
                });
                const renewed = response.headers.get('x-token');
                if (renewed) window.localStorage.setItem('token', renewed);
                if (!response.ok) throw new Error('catalog');
                const options = normalizeServerTtsVoices(await response.json());
                if (!current) return;
                setVoices(options); setError(!options.length);
                const baseLanguage = String(i18n.language || 'en').split('-')[0];
                setSelectedVoice(previous => {
                    const next = options.some(voice => voice.id === previous) ? previous
                        : options.find(voice => voice.language.split('-')[0] === baseLanguage)?.id || options[0]?.id || '';
                    if (next) window.localStorage.setItem(storageKey, next);
                    return next;
                });
            } catch { if (current) {setVoices([]); setError(true);} }
            finally { clearTimeout(timeout); if (current) setLoading(false); }
        })();
        return () => {current = false; clearTimeout(timeout); controller.abort();};
    }, [open, i18n.language, setSelectedVoice, storageKey]);
    return {voices, loading, error, valid: !loading && voices.some(voice => voice.id === selectedVoice)};
}
